/**
 * 德育评价类型 store 模块
 */
import {
  getEvaluationTypes,
  getEvaluationType,
  getEvaluationTypesWithProjects,
  createEvaluationType as apiCreate,
  updateEvaluationType as apiUpdate,
  deleteEvaluationType as apiDelete
} from '@/api/evaluationType'

/** 将 API 返回项转为前端格式 */
function normalizeType(raw) {
  if (!raw) return null
  return {
    id: raw.evaluation_type_id ?? raw.type_id ?? raw.id,
    name: raw.title ?? raw.name ?? raw.type_name ?? '',
    description: raw.description ?? '',
    order: raw.order_id ?? raw.order ?? raw.sort_order ?? 0
  }
}

/** 转为 API 请求格式（兼容 title/name、order/order_id） */
function toApiPayload(item) {
  const name = String(item.name ?? item.title ?? '').trim()
  const order = Number(item.order ?? item.order_id) || 0
  return {
    title: name,
    name: name,
    description: String(item.description ?? '').trim(),
    order,
    order_id: order
  }
}

export default {
  namespaced: true,
  state: {
    list: [],
    loading: false,
    error: null
  },
  getters: {
    evaluationTypes: state => state.list,
    loading: state => state.loading,
    error: state => state.error
  },
  mutations: {
    SET_LIST(state, list) {
      state.list = (list || []).map(normalizeType).filter(Boolean).sort((a, b) => a.order - b.order)
    },
    ADD_ITEM(state, item) {
      const n = normalizeType(item)
      if (n) {
        state.list.push(n)
        state.list.sort((a, b) => a.order - b.order)
      }
    },
    UPDATE_ITEM(state, { id, data }) {
      const idx = state.list.findIndex(t => String(t.id) === String(id))
      if (idx !== -1) {
        state.list[idx] = { ...state.list[idx], ...normalizeType(data) }
        state.list.sort((a, b) => a.order - b.order)
      }
    },
    REMOVE_ITEM(state, id) {
      state.list = state.list.filter(t => String(t.id) !== String(id))
    },
    SET_LOADING(state, v) {
      state.loading = !!v
    },
    SET_ERROR(state, err) {
      state.error = err
    }
  },
  actions: {
    /** 获取所有评价类型 */
    async fetchList({ commit }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await getEvaluationTypes()
        const list = res.data ?? res.list ?? (Array.isArray(res) ? res : [])
        commit('SET_LIST', list)
        return list
      } catch (e) {
        commit('SET_ERROR', e.message)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },
    /** 获取指定类型 */
    async fetchOne(_, typeId) {
      const res = await getEvaluationType(typeId)
      const data = res.data ?? res
      return normalizeType(data)
    },
    /** 获取类型及关联项目 */
    async fetchWithProjects({ commit }) {
      const res = await getEvaluationTypesWithProjects()
      const list = res.data ?? res.list ?? (Array.isArray(res) ? res : [])
      commit('SET_LIST', list)
      return list
    },
    /** 创建评价类型 */
    async create({ dispatch }, payload) {
      const data = toApiPayload(payload)
      await apiCreate(data)
      await dispatch('fetchList')
    },
    /** 更新评价类型 */
    async update({ dispatch }, { id, data }) {
      const payload = toApiPayload({ ...data, id })
      await apiUpdate(id, payload)
      await dispatch('fetchList')
    },
    /** 删除评价类型 */
    async remove({ dispatch }, id) {
      await apiDelete(id)
      await dispatch('fetchList')
    }
  }
}
